import { goShellLogin, sb } from '../data/supabase';

/**
 * 이 시스템 안에서만 통하는 권한.
 * ONE 플랫폼 전역 관리자 권한과는 별개다 (통합표준 5절).
 */
export type AppRole =
  | 'admin' // 시스템 관리자
  | 'cad_admin' // CAD 관리자 — 프로젝트 생성, 원본 등록, 최종 승인
  | 'designer' // 설계자
  | 'reviewer' // 운영 검토자
  | 'store' // 점포 사용자
  | 'vendor' // 협력사
  | 'auditor' // 감사 (읽기 전용)
  | 'user'; // 자동 등록 기본값 (최소 권한)

export interface AppUser {
  email: string;
  name: string | null;
  role: AppRole;
  dept: string | null;
  store_code: string | null;
  active: boolean;
}

export type BootState =
  /** 세션 확인 중 */
  | { status: 'loading' }
  /** 세션 없음 → 최상위 창을 ONE 쉘로 이동시키는 중 */
  | { status: 'redirecting' }
  /** 정상 진입 */
  | { status: 'ready'; user: AppUser }
  /** 로그인은 되었으나 이 시스템에서 비활성 처리된 계정 */
  | { status: 'inactive'; email: string }
  /** 부팅 실패 (네트워크/스키마 오류 등) */
  | { status: 'error'; message: string };

const USER_COLUMNS = 'email, name, role, dept, store_code, active';

async function fetchAppUser(email: string): Promise<AppUser | null> {
  const { data, error } = await sb()
    .from('cad_app_user')
    .select(USER_COLUMNS)
    .eq('email', email)
    .maybeSingle();

  if (error) throw new Error(`cad_app_user 조회 실패: ${error.message}`);
  return (data as AppUser | null) ?? null;
}

/**
 * 부팅 시퀀스 (통합표준 2절 / 5절)
 *
 *   1. 세션 확인만 한다. 로그인 화면은 만들지 않는다.
 *   2. 세션이 없으면 최상위 창을 ONE 쉘로 이동시킨다.
 *   3. 세션은 있는데 app_user 에 없으면 self_register() RPC 로 최소 권한 자동 등록 후 재조회한다.
 *      → "ONE 플랫폼 승인만 있으면 이 시스템도 자동으로 쓸 수 있음"을 만드는 핵심 단계.
 */
export async function boot(): Promise<BootState> {
  try {
    const { data, error } = await sb().auth.getSession();
    if (error) return { status: 'error', message: `세션 확인 실패: ${error.message}` };

    if (!data.session) {
      if (!goShellLogin()) {
        return {
          status: 'error',
          message:
            'ONE 플랫폼 주소(ONE_SHELL_URL)가 설정되지 않아 로그인 화면으로 이동할 수 없습니다. config.js 를 확인하세요.',
        };
      }
      return { status: 'redirecting' };
    }

    const email = (data.session.user.email ?? '').toLowerCase();
    if (!email) return { status: 'error', message: '세션에 이메일이 없습니다.' };

    let user = await fetchAppUser(email);

    if (!user) {
      // 관리자만 insert 가능한 RLS 정책이므로, SECURITY DEFINER 함수가 이 예외를 안전하게 뚫어준다.
      // ⚠️ 함수명이 cad_ 접두어인 이유는 supabase/migrations/0001 상단 주석 참고.
      const { error: rpcError } = await sb().rpc('cad_self_register');
      if (rpcError) {
        return { status: 'error', message: `사용자 자동 등록 실패: ${rpcError.message}` };
      }
      user = await fetchAppUser(email);
    }

    if (!user) {
      return { status: 'error', message: '사용자 등록 후에도 조회되지 않습니다.' };
    }
    if (!user.active) {
      return { status: 'inactive', email };
    }

    return { status: 'ready', user };
  } catch (e) {
    return { status: 'error', message: e instanceof Error ? e.message : String(e) };
  }
}

/** 세션이 만료되거나 다른 탭에서 로그아웃되면 쉘로 돌려보낸다. */
export function watchSession(onLost: () => void): () => void {
  const { data } = sb().auth.onAuthStateChange((event, session) => {
    if (event === 'SIGNED_OUT' || !session) onLost();
  });
  return () => data.subscription.unsubscribe();
}

export const ROLE_LABEL: Record<AppRole, string> = {
  admin: '시스템 관리자',
  cad_admin: 'CAD 관리자',
  designer: '설계자',
  reviewer: '검토자',
  store: '점포',
  vendor: '협력사',
  auditor: '감사',
  user: '일반',
};

export function isAdmin(user: AppUser): boolean {
  return user.role === 'admin' || user.role === 'cad_admin';
}

/** 도면을 편집할 수 있는 역할인지. 열람 전용 역할과 구분한다. */
export function canEdit(user: AppUser): boolean {
  return user.role === 'admin' || user.role === 'cad_admin' || user.role === 'designer';
}
